import { useEffect, useState } from 'react'

type SessionExpiryReason = 'inactivity' | 'absolute'

interface SessionExpiryDetail {
  remainingMs?: unknown
  reason?: unknown
}

interface SessionExpiryWarningState {
  remainingMs: number
  reason: SessionExpiryReason
}

function parseDetail(detail: SessionExpiryDetail): SessionExpiryWarningState | null {
  const remainingMs = typeof detail.remainingMs === 'number' ? detail.remainingMs : NaN
  if (!Number.isFinite(remainingMs) || remainingMs <= 0) return null

  return {
    remainingMs,
    reason: detail.reason === 'absolute' ? 'absolute' : 'inactivity',
  }
}

function formatRemainingTime(remainingMs: number): string {
  const minutes = Math.ceil(remainingMs / 60_000)
  return minutes <= 1 ? 'menos de un minuto' : `aproximadamente ${minutes} minutos`
}

export function SessionExpiryWarning() {
  const [warning, setWarning] = useState<SessionExpiryWarningState | null>(null)

  useEffect(() => {
    const handleSessionExpiring = (event: Event) => {
      const detail = (event as CustomEvent<SessionExpiryDetail>).detail
      const nextWarning = parseDetail(detail ?? {})
      if (nextWarning) setWarning(nextWarning)
    }

    window.addEventListener('auth-session-expiring', handleSessionExpiring)
    return () => window.removeEventListener('auth-session-expiring', handleSessionExpiring)
  }, [])

  if (!warning) return null

  const reason =
    warning.reason === 'absolute'
      ? 'el límite absoluto de cinco horas'
      : 'inactividad durante una hora'

  return (
    <div
      className="border-warning/40 bg-warning/10 text-foreground px-4 py-3 text-sm"
      role="alert"
      aria-live="assertive"
    >
      <p className="font-medium">Tu sesión está por expirar</p>
      <p>
        Expirará en {formatRemainingTime(warning.remainingMs)} por {reason}. Guarda tu trabajo antes
        de que termine la sesión.
      </p>
    </div>
  )
}
